import * as THREE from 'three';
import { Box } from '@/three/box';
import { Cylinder } from '@/three/cylinder';
import { InverseQuarterCylinder } from '@/three/inverseQuarterCylinder';
import { QuarterCylinder } from '@/three/quarterCylinder';
import type { SelectableObject } from '@/three/selectableObject';
import { Slope } from '@/three/slope';
import { SlopeCorner } from '@/three/slopeCorner';
import { SlopeEdge } from '@/three/slopeEdge';
import { Sphere } from '@/three/sphere';
import { Triangle } from '@/three/triangle';
import type { IconifyIcon } from '@iconify/react';
import z from 'zod';

export const Vec3MechanicSchema = z.codec(
  z.tuple([z.number(), z.number(), z.number()]),
  z.object({
    x: z.number(),
    y: z.number(),
    z: z.number(),
  }),
  {
    decode: ([x, y, z]) => new THREE.Vector3(x, y, z),
    encode: ({ x, y, z }): [number, number, number] => [x, y, z],
  },
);

/*
 * For some reason, the save file has it as (w, x, y, z) instead of (x, y, z, w).
 */
export const QuaternionMechanicSchema = z.codec(
  z.tuple([z.number(), z.number(), z.number(), z.number()]),
  z.object({
    x: z.number(),
    y: z.number(),
    z: z.number(),
    w: z.number(),
  }),
  {
    decode: ([w, x, y, z]) => new THREE.Quaternion(x, y, z, w),
    encode: ({ x, y, z, w }): [number, number, number, number] => [w, x, y, z],
  },
);

/**
 * Represents a mechanic in the game.
 */
export const MechanicSchema = z.object({
  /**
   * The name (ID) of the mechanic, e.g. "prGround", "prWall", "prTee", etc.
   */
  // TODO: Make this an enum of known mechanic types
  name: z.string(),
  /**
   * The position of the mechanic in 3D space.
   */
  pos: Vec3MechanicSchema,
  /**
   * The rotation of the mechanic in 3D space, represented as a quaternion.
   */
  rot: QuaternionMechanicSchema,
  saveRot: z.boolean(),
  /**
   * The scale of the mechanic in 3D space.
   */
  scale: Vec3MechanicSchema,
  saveScale: z.boolean(),
  /**
   * Any additional properties specific to the mechanic type.
   */
  // TODO: This should be a discriminated union of known mechanic types with their specific properties
  additional: z.unknown(),
  saveAdditional: z.boolean(),
});
export type MechanicType = z.infer<typeof MechanicSchema>;

export type MechanicConfig<SO extends typeof SelectableObject<any>> = {
  id: string;
  label: string;
  icon: string | IconifyIcon;
  clazz: SO;
  props: z.input<SO['schema']>;
  loadProps: z.input<SO['schema']>;
  toolProps: Omit<z.input<SO['schema']>, 'position' | 'scale' | 'offset'> & {
    position?: {
      x?: number;
      y?: number;
      z?: number;
    };
    scale?: {
      x?: number;
      y?: number;
      z?: number;
    };
    offset?: {
      x?: number;
      y?: number;
      z?: number;
    };
  };
  decode: (mechanic: MechanicType) => z.input<SO['schema']> | null;
  encode: (props: z.infer<SO['schema']>) => MechanicType;
  noSize?: boolean;
};

function m<SO extends typeof SelectableObject<any>>(
  config: Omit<
    MechanicConfig<SO>,
    'props' | 'loadProps' | 'toolProps' | 'decode' | 'encode'
  > & {
    props?: z.input<SO['schema']>;
    loadProps?: z.input<SO['schema']>;
    toolProps?: MechanicConfig<SO>['toolProps'];
    decode?: MechanicConfig<SO>['decode'];
    encode?: MechanicConfig<SO>['encode'];
  },
) {
  return {
    decode: (mechanic: any) => {
      return {
        position: mechanic.pos,
        quaternion: mechanic.rot,
        scale: mechanic.scale,
      };
    },
    encode: (props: any) => ({
      name: config.id,
      pos: props.position,
      savePos: false,
      rot: props.quaternion,
      saveRot: false,
      scale: props.scale,
      saveScale: false,
      additional: [],
      saveAdditional: false,
    }),
    toolProps: {},
    loadProps: {},
    ...config,
    props: {
      name: config.id,
      ...config.props,
    },
  };
}

const WALL_COLOR = 0x999999;
const ELEVATED_COLOR = 0xff7e5c;
const WATER_COLOR = 0x4f8cff;

// null entries are just visual separators in the item panel
export const MECHANICS = [
  //#region Walls
  m({
    id: 'prWall',
    label: 'Wall',
    icon: 'mdi:cube',
    clazz: Box,
    props: {
      color: WALL_COLOR,
    },
  }),
  m({
    id: 'prWall2',
    label: 'Triangle Wall',
    icon: 'mdi:triangle',
    clazz: Triangle,
    props: {
      color: WALL_COLOR,
    },
  }),
  m({
    id: 'prWall3',
    label: 'Quarter Circle Wall',
    icon: 'bxs:circle-quarter',
    clazz: QuarterCylinder,
    props: {
      color: WALL_COLOR,
    },
  }),
  m({
    id: 'prWall4',
    label: 'Inverse Quarter Circle Wall',
    icon: {
      body: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M0 0H24V24A24 24 0 000 0Z"/></svg>',
    },
    clazz: InverseQuarterCylinder,
    props: {
      color: WALL_COLOR,
    },
  }),
  //#endregion
  null,
  //#region Elevated
  m({
    id: 'prElevated',
    label: 'Elevated',
    icon: 'mdi:cube',
    clazz: Box,
    props: {
      color: ELEVATED_COLOR,
    },
  }),
  m({
    id: 'prElevated2',
    label: 'Slope',
    icon: 'mdi:slope-uphill',
    clazz: Slope,
    props: {
      color: ELEVATED_COLOR,
    },
  }),
  m({
    id: 'prElevated3',
    label: 'Slope Corner',
    icon: 'mdi:slope-uphill',
    clazz: SlopeCorner,
    props: {
      color: ELEVATED_COLOR,
    },
  }),
  // prElevated4 is an unused quarter pipe that doesn't behave correctly
  m({
    id: 'prElevated5',
    label: 'Slope Edge',
    icon: 'mdi:slope-uphill',
    clazz: SlopeEdge,
    props: {
      color: ELEVATED_COLOR,
    },
  }),
  //#endregion
  null,
  //#region Water
  m({
    id: 'prWater',
    label: 'Water',
    icon: 'mdi:water',
    clazz: Box,
    props: {
      color: WATER_COLOR,
    },
    toolProps: {
      position: {
        y: -0.9900000095367432,
      },
      scale: { y: 2 },
    },
  }),
  m({
    id: 'prWater2',
    label: 'Triangle Water',
    icon: 'mdi:water',
    clazz: Triangle,
    props: {
      color: WATER_COLOR,
    },
    toolProps: {
      position: {
        y: -0.9900000095367432,
      },
      scale: { y: 2 },
    },
  }),
  m({
    id: 'prWater3',
    label: 'Circle Water',
    icon: 'mdi:water',
    clazz: Cylinder,
    props: {
      color: WATER_COLOR,
    },
    toolProps: {
      position: {
        y: -0.9900000095367432,
      },
      scale: { y: 2 },
    },
  }),
  //#endregion
  null,
  //#region Other
  // Main
  m({
    id: 'prHole',
    label: 'Hole',
    icon: 'mdi:golf',
    clazz: Cylinder,
    props: {
      color: 0x000000,
      radius: 1,
      height: 0.01,
    },
    toolProps: {
      position: { y: 0 },
    },
    noSize: true,
  }),
  m({
    id: 'prTee',
    label: 'Tee',
    icon: 'mdi:circle-small',
    clazz: Box,
    props: {
      color: 0xffa550,
      size: new THREE.Vector3(1, 1, 0.01),
    },
    toolProps: {
      scale: { x: 2.9093751907348633, y: 2.078125, z: 1 },
      position: { y: Math.fround(0.05) },
      quaternion: new THREE.Quaternion(
        0.7071068286895752,
        0,
        0,
        0.7071068286895752,
      ),
    },
    noSize: true,
  }),
  // Creatures
  m({
    id: 'prToucan',
    label: 'Toucan',
    icon: 'mdi:bird',
    clazz: Cylinder,
    props: {
      color: 0xfda720,
      radius: 1 / 5,
      height: 2 / 5,
      offset: { y: 1 / 5 },
    },
    toolProps: {
      scale: { x: 5, y: 5, z: 5 },
      position: { y: 0 },
    },
    noSize: true,
  }),
  m({
    id: 'prPorcupine',
    label: 'Porcupine',
    icon: 'mdi:paw',
    clazz: Sphere,
    props: {
      color: 0x8b4513,
      offset: { y: 0.5 },
    },
    toolProps: {
      scale: { x: 2, y: 2, z: 2 },
      position: { y: 0 },
    },
    noSize: true,
  }),
  // Soccer
  m({
    id: 'prSoccerBall',
    label: 'Soccer Ball',
    icon: 'mdi:soccer',
    clazz: Sphere,
    props: {
      color: 0xffffff,
      radius: 1 / 8,
    },
    toolProps: {
      scale: new THREE.Vector3(8, 8, 8),
    },
    noSize: true,
  }),
  m({
    id: 'prSoccerGoal',
    label: 'Soccer Goal',
    icon: 'mdi:soccer',
    clazz: Box,
    props: {
      color: 0xffffff,
      size: new THREE.Vector3(6, 4.34, 1),
    },
  }),
  //#endregion
] as const;
export const MECHANIC_MAP = Object.fromEntries(
  MECHANICS.filter(m => !!m).map(m => [m.id, m]),
) as Record<string, (typeof MECHANICS)[number]>;

export function getMechanicConfig(id: string): MechanicConfig<any> | null {
  return MECHANIC_MAP[id] || null;
}

export const UNKNOWN_MECHANIC_CONFIG: MechanicConfig<typeof Box> = {
  id: 'unknown',
  label: 'Unknown',
  icon: 'mdi:help',
  clazz: Box,
  props: {},
  loadProps: {},
  toolProps: {},
  decode: mechanic => {
    return {
      name: mechanic.name,
      position: mechanic.pos,
      quaternion: mechanic.rot,
      scale: mechanic.scale,
      extra: {
        additional: mechanic.additional,
        saveAdditional: mechanic.saveAdditional,
      },
    };
  },
  encode: props => ({
    name: props.name,
    pos: props.position,
    savePos: false,
    rot: props.quaternion,
    saveRot: false,
    scale: props.scale,
    saveScale: false,
    additional: props.extra.additional,
    saveAdditional: props.extra.saveAdditional,
  }),
};
